import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  DialogContent,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import DialogTitle from "../../../components/dialog-title";
import userFlowService from "../../../services/user-flow.service";
import { dateFormat } from "../../../helpers/date-helpers";

const useStyles = makeStyles((theme) => ({
  loading: {
    minHeight: 260,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statCard: {
    height: "100%",
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  section: {
    marginTop: theme.spacing(3),
  },
  path: {
    direction: "ltr",
    textAlign: "left",
    fontFamily: "monospace",
    whiteSpace: "normal",
  },
  meta: {
    direction: "ltr",
    textAlign: "left",
    maxWidth: 260,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const emptyDetails = {
  stats: {},
  identities: [],
  sessions: [],
  popularPaths: [],
  buttonClicks: [],
};

function formatDuration(ms) {
  if (!ms) return "۰ ثانیه";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} ثانیه`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} دقیقه و ${seconds % 60} ثانیه`;
}

function StatCard({ label, value }) {
  const classes = useStyles();
  return (
    <Card className={classes.statCard}>
      <CardContent>
        <Typography className={classes.statLabel} variant="body2">
          {label}
        </Typography>
        <Typography variant="h6">{value}</Typography>
      </CardContent>
    </Card>
  );
}

function EmptyRow({ colSpan, text }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} align="center">
        {text}
      </TableCell>
    </TableRow>
  );
}

export default function UserFlowModal({ data, close }) {
  const classes = useStyles();
  const [details, setDetails] = useState(emptyDetails);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    userFlowService.getUserDetails(data.id).then((res) => {
      if (!mounted) return;
      if (res.error) {
        setError("امکان دریافت جزئیات نشست‌های کاربر وجود ندارد.");
        setLoading(false);
        return;
      }
      setDetails(res.data || emptyDetails);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [data.id]);

  const stats = details.stats || {};

  return (
    <>
      <DialogTitle onClose={close} divider>
        جزئیات نشست‌ها و مسیرهای کاربر
      </DialogTitle>
      <DialogContent className="py-3">
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : error ? (
          <Paper className="p-3" variant="outlined">
            <Typography color="error">{error}</Typography>
          </Paper>
        ) : (
          <>
            <Box mb={2}>
              <Typography variant="subtitle1">
                {data.fullName || data.phoneNumber || data.id}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                شناسه کاربر: <span className="en">{data.id}</span>
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard label="تعداد نشست‌ها" value={stats.sessionsCount || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard label="کدهای visitor" value={stats.visitorsCount || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard label="بازدید صفحه" value={stats.pageViewsCount || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard label="کلیک دکمه" value={stats.buttonClicksCount || 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="میانگین زمان نشست"
                  value={formatDuration(stats.averageSessionDuration)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="مجموع زمان حضور"
                  value={formatDuration(stats.totalDuration)}
                />
              </Grid>
            </Grid>

            <Box className={classes.section}>
              <Typography variant="h6">اطلاعات دستگاه و مرورگر</Typography>
              <Divider className="my-2" />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>visitorId</TableCell>
                    <TableCell>دستگاه</TableCell>
                    <TableCell>مرورگر</TableCell>
                    <TableCell>سیستم‌عامل</TableCell>
                    <TableCell>زبان</TableCell>
                    <TableCell>آخرین مشاهده</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.identities?.length ? (
                    details.identities.map((item) => (
                      <TableRow key={item.visitorId}>
                        <TableCell className={classes.meta}>{item.visitorId}</TableCell>
                        <TableCell>{item.deviceType || "-"}</TableCell>
                        <TableCell>
                          {[item.browserName, item.browserVersion].filter(Boolean).join(" ") || "-"}
                        </TableCell>
                        <TableCell>
                          {[item.osName, item.osVersion].filter(Boolean).join(" ") || "-"}
                        </TableCell>
                        <TableCell>{item.language || "-"}</TableCell>
                        <TableCell>{item.lastSeenAt ? dateFormat(item.lastSeenAt) : "-"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <EmptyRow colSpan={6} text="اطلاعاتی ثبت نشده است." />
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box className={classes.section}>
              <Typography variant="h6">مسیرهای پرتکرار</Typography>
              <Divider className="my-2" />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>مسیر</TableCell>
                    <TableCell>تعداد تکرار</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.popularPaths?.length ? (
                    details.popularPaths.map((item) => (
                      <TableRow key={item.path}>
                        <TableCell className={classes.path}>{item.path}</TableCell>
                        <TableCell>{item.count}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <EmptyRow colSpan={2} text="مسیر پرتکراری ثبت نشده است." />
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box className={classes.section}>
              <Typography variant="h6">نشست‌ها</Typography>
              <Divider className="my-2" />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>sessionId</TableCell>
                    <TableCell>شروع</TableCell>
                    <TableCell>زمان حضور</TableCell>
                    <TableCell>صفحات</TableCell>
                    <TableCell>کلیک‌ها</TableCell>
                    <TableCell>مسیر</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.sessions?.length ? (
                    details.sessions.map((session) => (
                      <TableRow key={session.sessionId}>
                        <TableCell className={classes.meta}>{session.sessionId}</TableCell>
                        <TableCell>{session.startedAt ? dateFormat(session.startedAt) : "-"}</TableCell>
                        <TableCell>{formatDuration(session.durationMs)}</TableCell>
                        <TableCell>{session.pageViewsCount || 0}</TableCell>
                        <TableCell>{session.buttonClicksCount || 0}</TableCell>
                        <TableCell className={classes.path}>
                          {(session.path || []).join(" -> ") || "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <EmptyRow colSpan={6} text="نشستی ثبت نشده است." />
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box className={classes.section}>
              <Typography variant="h6">کلیک‌های پرتکرار کاربر</Typography>
              <Divider className="my-2" />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>نام رویداد</TableCell>
                    <TableCell>تعداد کلیک</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.buttonClicks?.length ? (
                    details.buttonClicks.map((item) => (
                      <TableRow key={item.eventName}>
                        <TableCell>{item.eventName}</TableCell>
                        <TableCell>{item.clickCount}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <EmptyRow colSpan={2} text="کلیکی ثبت نشده است." />
                  )}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
    </>
  );
}
